import { makeStyles } from "tss-react/mui";
import {
  MiAlert,
  MiButton,
  MiIcon,
  MiPlaceholderIcon,
  MiTab,
} from "@miplatform/components";
import { BUTTON_VARIANTS, ICONS_WEB } from "@miplatform/constants";
import * as mdiIcons from "@mdi/js";
import {
  Autocomplete,
  Box,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { TransferList } from "@mibuildweb/components";
import { ConfigAccordionHeader } from "../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { useCommunityInspectionAuthoritiesTabViewModel } from "./useCommunityInspectionAuthoritiesTabViewModel";

const mdi = mdiIcons as Record<string, string>;

interface ICommunityInspectionAuthoritiesTabProps {
  communityId: string;
}

export function CommunityInspectionAuthoritiesTab({
  communityId,
}: Readonly<ICommunityInspectionAuthoritiesTabProps>) {
  const { classes, theme } = useStyles();

  const {
    activeAuthorityId,
    isAddingAuthority,
    setIsAddingAuthority,
    communityInspectionAuthorities,
    isLoading,
    availableToAdd,
    authoritySearch,
    setAuthoritySearch,
    isLoadingAuthorities,
    authorityTabs,
    availableInspections,
    selectedInspections,
    handleAuthorityTabChange,
    handleAddAuthority,
    handleRemoveAuthority,
    handleAddTemplate,
    handleRemoveTemplate,
  } = useCommunityInspectionAuthoritiesTabViewModel(communityId);

  return (
    <Box className={classes.container}>
      {(communityInspectionAuthorities.length > 0 || isAddingAuthority) && (
        <MiAlert
          description="Select which inspections are performed by each inspection authority"
          storageKey="community-inspection-authorities-info-alert"
        />
      )}

      <ConfigAccordionHeader
        title="Inspection Authorities"
        customAction={
          <MiButton
            title="+ Add Inspection Authority"
            variant={BUTTON_VARIANTS.CONTAINED}
            onClick={() => setIsAddingAuthority(true)}
            disabled={isLoading}
          />
        }
      >
        <Box className={classes.authorityList}>
          {communityInspectionAuthorities.length === 0 && !isAddingAuthority && (
            <MiPlaceholderIcon
              icon={mdi.mdiClipboardSearchOutline}
              text="No Inspection Authorities"
            />
          )}
          {communityInspectionAuthorities.map((authority) => (
            <Box key={authority.id} className={classes.authorityRow}>
              <TextField
                label="Inspection Authority *"
                value={authority.inspectionAuthorityName}
                disabled
                fullWidth
                size="small"
              />
              <IconButton
                aria-label={`Remove ${authority.inspectionAuthorityName}`}
                onClick={() => handleRemoveAuthority(authority.id)}
                disabled={isLoading}
                size="small"
              >
                <MiIcon
                  path={mdi[ICONS_WEB.MINUS_CIRCLE]}
                  color={theme.palette.red.main}
                />
              </IconButton>
            </Box>
          ))}
          {isAddingAuthority && (
            <Box className={classes.authorityRow}>
              <Autocomplete
                options={availableToAdd}
                getOptionLabel={(o) => o.name}
                inputValue={authoritySearch}
                onInputChange={(_, value) => setAuthoritySearch(value)}
                filterOptions={(x) => x}
                loading={isLoadingAuthorities}
                onChange={(_, value) => {
                  if (value) handleAddAuthority(value.id);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Inspection Authority *"
                    size="small"
                    autoFocus
                  />
                )}
                fullWidth
              />
              <IconButton
                aria-label="Cancel adding inspection authority"
                onClick={() => setIsAddingAuthority(false)}
                size="small"
              >
                <MiIcon
                  path={mdi[ICONS_WEB.MINUS_CIRCLE]}
                  color={theme.palette.red.main}
                />
              </IconButton>
            </Box>
          )}
        </Box>
      </ConfigAccordionHeader>

      <ConfigAccordionHeader title="Inspections">
        {communityInspectionAuthorities.length === 0 ? (
          <Box className={classes.emptyInspections}>
            <Typography variant="body1" fontWeight={500}>
              No Inspections to Assign
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You must first add an inspection authority before you can assign
              inspections to them.
            </Typography>
          </Box>
        ) : (
          <Box>
            <MiTab
              tabs={authorityTabs}
              currentTab={activeAuthorityId ?? ""}
              onTabChange={handleAuthorityTabChange}
              chipStyle={{
                marginLeft: "8px !important",
                backgroundColor: theme.palette.grey.medium,
                color: theme.palette.common.white,
              }}
            />
            <Box className={classes.transferListContainer}>
              <TransferList
                showAvailableItemsSearch
                availableItems={availableInspections}
                selectedItems={selectedInspections}
                onAddItem={handleAddTemplate}
                onRemoveItem={handleRemoveTemplate}
                availableTitle="Available Inspections"
                selectedTitle="Selected Inspections"
                emptyAvailableListMessage="No inspections available."
                emptySelectedListMessage="No inspections selected."
                disabled={isLoading}
              />
            </Box>
          </Box>
        )}
      </ConfigAccordionHeader>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    overflow: "auto",
    flex: 1,
  },
  authorityList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    padding: theme.spacing(2),
  },
  authorityRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  transferListContainer: {
    padding: theme.spacing(2),
  },
  emptyInspections: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(4),
    textAlign: "center",
  },
}));
